/**
 * Redesigned Projects Page
 * Displays enriched project data from the dashboard API for designers/contractors
 */

import {
	Briefcase,
	CheckCircle2,
	Clock,
	IndianRupee,
	Timer,
} from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { QueryErrorState } from "@/components/ui/query-error-state";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthStore } from "@/features/auth/store/auth.store";
import type { DashboardProject } from "@/features/projects/services/projects-api.service";
import {
	Avatar,
	Eyebrow,
	FloatPanel,
	Icn,
	ItalicAccent,
	Pill,
	Progress,
	StudioButton,
} from "@/features/studio/theme";
import { formatPrice } from "@/lib/format";
import { useProjectsDashboard } from "../hooks/use-projects";

const ASSETS_URL = import.meta.env.VITE_ASSETS_URL as string;

const STATUS_DISPLAY: Record<string, string> = {
	draft: "Draft",
	seeking_designer: "Seeking Designer",
	awaiting_funding: "Awaiting Funding",
	in_progress: "Active",
	completed: "Completed",
	cancelled: "Cancelled",
};

const TAB_FILTERS: Record<string, string[]> = {
	All: [],
	Active: ["in_progress"],
	Pending: ["draft", "seeking_designer", "awaiting_funding"],
	Completed: ["completed"],
};

const BUDGET_RANGES = [
	{ label: "All Budgets", min: 0, max: Infinity },
	{ label: "Under 10L", min: 0, max: 1000000 },
	{ label: "10L - 25L", min: 1000000, max: 2500000 },
	{ label: "25L - 50L", min: 2500000, max: 5000000 },
	{ label: "Above 50L", min: 5000000, max: Infinity },
];

function getActionsForStatus(
	status: string,
	userType: string | null | undefined,
	projectId: string,
	navigate: (path: string) => void,
) {
	const actions: {
		label: string;
		onClick: () => void;
		variant?: "default" | "primary" | "outline";
	}[] = [
		{
			label: "View Details",
			onClick: () => navigate(`/projects/${projectId}`),
			variant: "outline" as const,
		},
	];

	if (status === "in_progress" && userType === "designer") {
		actions.push({
			label: "Upload BOQ",
			onClick: () => navigate(`/projects/${projectId}?tab=boq`),
			variant: "primary" as const,
		});
	}
	if (status === "in_progress" && userType === "contractor") {
		actions.push({
			label: "Submit Quote",
			onClick: () => navigate(`/projects/${projectId}`),
			variant: "primary" as const,
		});
	}

	return actions;
}

// ---------- Custom ProjCard Component ----------
interface ProjCardProps {
	p: DashboardProject;
	userType: string | null | undefined;
	navigate: (path: string) => void;
	accent: string;
}

function ProjCard({ p, userType, navigate, accent }: ProjCardProps) {
	const [hover, setHover] = useState(false);
	const stage = STATUS_DISPLAY[p.status] || p.status;
	const isPending =
		p.status === "seeking_designer" ||
		p.status === "awaiting_funding" ||
		p.status === "draft";

	const images = [
		`${ASSETS_URL}livingroom.webp`,
		`${ASSETS_URL}2bhk.webp`,
		`${ASSETS_URL}dinningroom.webp`,
		`${ASSETS_URL}bedroom.webp`,
		`${ASSETS_URL}kitchen.webp`,
	];
	const imgIdx =
		Math.abs(
			p.id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0),
		) % images.length;
	const coverImg = images[imgIdx];

	const location =
		[p.property?.locality, p.property?.city].filter(Boolean).join(", ") ||
		"Location not set";

	const projectActions = getActionsForStatus(
		p.status,
		userType,
		p.id,
		navigate,
	);

	return (
		// biome-ignore lint/a11y/useSemanticElements: custom interactive card
		<div
			role="button"
			tabIndex={0}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			onClick={() => navigate(`/projects/${p.id}`)}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					navigate(`/projects/${p.id}`);
				}
			}}
			style={{
				cursor: "pointer",
				transform: hover ? "translateY(-4px)" : "none",
				transition: "all 350ms cubic-bezier(0.22,1,0.36,1)",
			}}
		>
			<FloatPanel
				elevation={hover ? 3 : 1}
				padding={0}
				style={{
					overflow: "hidden",
				}}
			>
				<div
					style={{
						position: "relative",
						aspectRatio: "16/9",
						overflow: "hidden",
					}}
				>
					<img
						src={coverImg}
						alt=""
						style={{
							width: "100%",
							height: "100%",
							objectFit: "cover",
							filter: "saturate(0.92) contrast(1.06)",
							transform: hover ? "scale(1.05)" : "scale(1)",
							transition: "transform 1000ms cubic-bezier(0.22,1,0.36,1)",
						}}
					/>
					<div
						style={{
							position: "absolute",
							inset: 0,
							background:
								"linear-gradient(180deg, color-mix(in srgb, var(--color-dashboard-scrim) 25%, transparent), transparent 40%, color-mix(in srgb, var(--color-dashboard-scrim) 72%, transparent))",
						}}
					/>
					<div
						style={{
							position: "absolute",
							top: 16,
							left: 16,
							display: "flex",
							gap: 8,
						}}
					>
						<Pill
							tone="active"
							style={{
								background:
									"color-mix(in srgb, var(--color-dashboard-paper) 92%, transparent)",
							}}
						>
							{stage}
						</Pill>
					</div>
					<div
						style={{
							position: "absolute",
							top: 16,
							right: 16,
							display: "flex",
						}}
					>
						{["AM", "RC"].map((t, idx) => (
							<Avatar
								key={t}
								initials={t}
								tone={idx === 0 ? "ink" : "cognac"}
								size={28}
								ring
								style={{
									marginLeft: idx ? -10 : 0,
								}}
							/>
						))}
					</div>
					<div
						style={{
							position: "absolute",
							left: 18,
							bottom: 16,
							right: 18,
							color: "var(--color-dashboard-on-fill)",
						}}
					>
						<Eyebrow
							style={{
								color:
									"color-mix(in srgb, var(--color-dashboard-on-fill) 70%, transparent)",
								fontSize: 9,
							}}
						>
							<span>{location}</span>
							{p.property?.sizeSqft && (
								<>
									<span> · </span>
									<span>{p.property.sizeSqft} sq ft</span>
								</>
							)}
							<span> · </span>
							<span>{p.client.name}</span>
						</Eyebrow>
						<div
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 26,
								letterSpacing: "-0.015em",
								marginTop: 3,
								textShadow: "0 2px 12px rgba(0,0,0,0.4)",
							}}
						>
							{p.title}
						</div>
					</div>
				</div>
				<div style={{ padding: "18px 22px 22px" }}>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "baseline",
							marginBottom: 10,
						}}
					>
						<span
							style={{
								fontFamily: "var(--font-mono)",
								fontSize: 9,
								letterSpacing: "0.12em",
								color: "var(--color-dashboard-muted)",
							}}
						>
							MILESTONE PROGRESS
						</span>
						<span
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 13,
								color: "var(--color-dashboard-muted-dark)",
							}}
						>
							<strong
								style={{
									color: "var(--color-dashboard-text)",
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{p.progress.completed}/{p.progress.total}
							</strong>{" "}
							milestones completed
						</span>
					</div>
					<Progress
						value={p.progress.percentage}
						fillColor={accent}
						height={5}
					/>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							marginTop: 14,
						}}
					>
						<span
							style={{
								display: "flex",
								alignItems: "center",
								gap: 6,
								fontFamily: "var(--font-body)",
								fontSize: 12,
								color: isPending
									? "var(--color-dashboard-pill-awaiting)"
									: "var(--color-dashboard-success)",
							}}
						>
							<Icn name={isPending ? "clock" : "check"} size={14} />
							{isPending ? "Pending activation" : "Active project"}
						</span>
						<div style={{ display: "flex", gap: 8 }}>
							{projectActions.map((act) => (
								<StudioButton
									key={act.label}
									variant={act.variant === "primary" ? "primary" : "secondary"}
									size="sm"
									onClick={(e) => {
										e.stopPropagation();
										act.onClick();
									}}
								>
									{act.label}
								</StudioButton>
							))}
						</div>
					</div>
				</div>
			</FloatPanel>
		</div>
	);
}

// ---------- Custom StatCard Component ----------
interface StatCardProps {
	label: string;
	value: string | number;
	icon: React.ComponentType<{ className?: string }>;
}

function StatCard({ label, value, icon: Icon }: StatCardProps) {
	return (
		<FloatPanel
			elevation={1}
			padding="16px 20px"
			style={{ background: "var(--color-dashboard-surface)", flex: 1 }}
		>
			<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 6,
						color: "var(--color-dashboard-muted)",
					}}
				>
					<Icon className="h-4.5 w-4.5 text-[var(--color-dashboard-muted)]" />
					<span
						style={{
							fontFamily: "var(--font-mono)",
							fontSize: 9,
							letterSpacing: "0.08em",
							textTransform: "uppercase",
						}}
					>
						{label}
					</span>
				</div>
				<span
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 22,
						color: "var(--color-dashboard-text)",
						letterSpacing: "-0.01em",
						marginTop: 2,
					}}
				>
					{value}
				</span>
			</div>
		</FloatPanel>
	);
}

// ---------- Main Redesigned Page Component ----------
export function ProjectsPageRedesign() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuthStore();
	const [activeTab, setActiveTab] = useState<string>("All");
	const [locationFilter, setLocationFilter] = useState("All");
	const [budgetFilter, setBudgetFilter] = useState("All Budgets");
	const { data, isLoading, isError, refetch, isFetching } =
		useProjectsDashboard();
	const accent = "var(--color-dashboard-accent)";

	const filteredProjects = useMemo(() => {
		if (!data) return [];

		let projects = data.projects;

		// Tab filter
		const statusFilter = TAB_FILTERS[activeTab];
		if (statusFilter && statusFilter.length > 0) {
			projects = projects.filter((p) => statusFilter.includes(p.status));
		}

		// Location filter
		if (locationFilter !== "All") {
			projects = projects.filter((p) => p.property?.city === locationFilter);
		}

		// Budget filter
		const budgetRange = BUDGET_RANGES.find((r) => r.label === budgetFilter);
		if (budgetRange && budgetFilter !== "All Budgets") {
			projects = projects.filter((p) => {
				const budget = p.budgetMax || p.budgetMin || 0;
				return budget >= budgetRange.min && budget <= budgetRange.max;
			});
		}

		return projects;
	}, [data, activeTab, locationFilter, budgetFilter]);

	if (isLoading) {
		return (
			<div className="space-y-6 p-12">
				<div className="flex items-center justify-between">
					<Skeleton className="h-8 w-48" />
				</div>
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
					{["stat-1", "stat-2", "stat-3", "stat-4", "stat-5"].map((id) => (
						<Skeleton key={id} className="h-20" />
					))}
				</div>
				<div className="grid gap-4 lg:grid-cols-2">
					{["card-1", "card-2", "card-3", "card-4"].map((id) => (
						<Skeleton key={id} className="h-64" />
					))}
				</div>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="p-12">
				<QueryErrorState
					message={t(
						"projects.loadError",
						"Failed to load projects. Please try again.",
					)}
					onRetry={() => refetch()}
					isRetrying={isFetching}
				/>
			</div>
		);
	}

	if (!data || data.projects.length === 0) {
		return (
			<div className="space-y-6 p-12">
				<Eyebrow style={{ fontSize: 10, letterSpacing: "0.22em" }}>
					PORTFOLIO IN PROGRESS
				</Eyebrow>
				<h1
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 48,
						color: "var(--color-dashboard-text)",
						margin: "12px 0 0",
					}}
				>
					Projects library.
				</h1>
				<div
					style={{
						display: "flex",
						height: 240,
						alignItems: "center",
						justifyContent: "center",
						borderRadius: 13,
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
					}}
				>
					<div style={{ textAlign: "center" }}>
						<Briefcase className="mx-auto h-10 w-10 text-muted-foreground/50" />
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								fontWeight: 600,
								color: "var(--color-dashboard-text)",
								marginTop: 12,
							}}
						>
							{t("projects.noProjects", "No projects yet")}
						</p>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 12.5,
								color: "var(--color-dashboard-muted)",
								marginTop: 4,
							}}
						>
							{t(
								"projects.noProjectsHint",
								"Projects assigned to you will appear here.",
							)}
						</p>
					</div>
				</div>
			</div>
		);
	}

	const { stats, filterOptions } = data;

	return (
		<div className="space-y-8 p-12">
			{/* Header */}
			<section
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-end",
					gap: 24,
					flexWrap: "wrap",
				}}
			>
				<div>
					<Eyebrow style={{ fontSize: 10, letterSpacing: "0.22em" }}>
						MEHRA ATELIER · PORTFOLIO IN PROGRESS
					</Eyebrow>
					<h1
						style={{
							fontFamily: "var(--font-display)",
							fontWeight: 500,
							fontSize: 48,
							lineHeight: 1.0,
							letterSpacing: "-0.025em",
							color: "var(--color-dashboard-text)",
							margin: "12px 0 0",
						}}
					>
						{activeTab === "Completed" ? (
							<>
								Homes we{" "}
								<ItalicAccent style={{ fontSize: 48, color: accent }}>
									finished
								</ItalicAccent>
								.
							</>
						) : (
							<>
								{filteredProjects.length} homes,{" "}
								<ItalicAccent style={{ fontSize: 48, color: accent }}>
									becoming
								</ItalicAccent>
								.
							</>
						)}
					</h1>
				</div>
			</section>

			{/* Summary Stats */}
			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
				<StatCard
					icon={Briefcase}
					label={t("projects.activeProjects", "Active Projects")}
					value={stats.active}
				/>
				<StatCard
					icon={Clock}
					label={t("projects.pendingApproval", "Pending Approval")}
					value={stats.pendingApproval}
				/>
				<StatCard
					icon={CheckCircle2}
					label={t("projects.completed", "Completed")}
					value={stats.completed}
				/>
				<StatCard
					icon={IndianRupee}
					label={t("projects.totalValue", "Total Project Value")}
					value={formatPrice(stats.totalProjectValue)}
				/>
				<StatCard
					icon={Timer}
					label={t("projects.paymentsPending", "Payments Pending")}
					value={formatPrice(stats.paymentsPending)}
				/>
			</div>

			{/* Filters controls bar */}
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					gap: 16,
					flexWrap: "wrap",
					borderBottom: "1px solid var(--color-dashboard-border)",
					paddingBottom: 16,
				}}
			>
				{/* Custom Tab list styled like chips */}
				<Tabs
					value={activeTab}
					onValueChange={setActiveTab}
					className="w-auto border-none p-0 bg-transparent"
				>
					<TabsList className="flex gap-2 p-0 bg-transparent border-none">
						{Object.keys(TAB_FILTERS).map((tab) => {
							const on = activeTab === tab;
							return (
								<TabsTrigger
									key={tab}
									value={tab}
									style={{
										height: 32,
										padding: "0 14px",
										borderRadius: 999,
										fontFamily: "var(--font-body)",
										fontSize: 12,
										fontWeight: on ? 600 : 500,
										color: on
											? "var(--color-dashboard-on-fill)"
											: "var(--color-dashboard-muted-dark)",
										background: on
											? "var(--color-dashboard-accent)"
											: "transparent",
										border: on
											? "none"
											: "1px solid var(--color-dashboard-border)",
										cursor: "pointer",
										transition: "all 200ms ease",
										boxShadow: "none",
									}}
								>
									{t(`projects.tab.${tab.toLowerCase()}`, tab)}
								</TabsTrigger>
							);
						})}
					</TabsList>
				</Tabs>

				{/* Location / Budget select dropdowns */}
				<div className="w-full md:w-auto" style={{ display: "flex", gap: 10 }}>
					<Select value={locationFilter} onValueChange={setLocationFilter}>
						<SelectTrigger className="min-w-0 flex-1 md:w-45 md:min-w-auto md:flex-initial bg-[var(--color-dashboard-paper)] border-[var(--color-dashboard-border)] text-sm text-[var(--color-dashboard-text)]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="All">
								{t("projects.allLocations", "All Locations")}
							</SelectItem>
							{filterOptions.cities.map((city) => (
								<SelectItem key={city} value={city}>
									{city}
								</SelectItem>
							))}
						</SelectContent>
					</Select>

					<Select value={budgetFilter} onValueChange={setBudgetFilter}>
						<SelectTrigger className="min-w-0 flex-1 md:w-45 md:min-w-auto md:flex-initial bg-[var(--color-dashboard-paper)] border-[var(--color-dashboard-border)] text-sm text-[var(--color-dashboard-text)]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{BUDGET_RANGES.map((range) => (
								<SelectItem key={range.label} value={range.label}>
									{range.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{/* Projects list grid */}
			{filteredProjects.length === 0 ? (
				<div
					style={{
						display: "flex",
						height: 160,
						alignItems: "center",
						justifyContent: "center",
						borderRadius: 13,
						background: "var(--color-dashboard-paper)",
						border: "1px solid var(--color-dashboard-border)",
					}}
				>
					<p
						style={{
							fontFamily: "var(--font-body)",
							fontSize: 14,
							color: "var(--color-dashboard-muted)",
						}}
					>
						{t(
							"projects.noMatchingProjects",
							"No projects match the selected filters.",
						)}
					</p>
				</div>
			) : (
				<div className="grid gap-6 md:grid-cols-2">
					{filteredProjects.map((project) => (
						<ProjCard
							key={project.id}
							p={project}
							userType={user?.userType}
							navigate={navigate}
							accent={accent}
						/>
					))}
				</div>
			)}
		</div>
	);
}
